// Translations load only from the V4 backend: every key not marked reused needs seeding there.

import {Colors} from '../../utils/style/colors';
import {RESPONSE_STATUS, TIMELINE} from '../../utils/models/v4AlarmSituation';

export const SITUATION_TEXT = {
    noRecipients: ['noRecipients', 'No recipients'], // reused
    noneAccepted: ['assistanceResponse.noneAccepted', 'No one accepted yet'],
    partial: ['assistanceResponse.partial', '{accepted} of {needed} needed accepted'],
    reached: ['assistanceResponse.reached', 'Help on its way'],
    reachedCount: ['assistanceResponse.reachedCount', 'Help on its way, {accepted} of {needed} accepted'],
    accepted: ['assistanceResponse.accepted', '{accepted} of {total} accepted'],
    responded: ['assistanceResponse.responded', '{responded} of {total} responded'],
    closed: ['closed', 'Closed'], // reused

    requestCount: ['assistanceResponse.requestCount', '{count} assistance request(s)'],
    arcNotified: ['assistanceResponse.arcNotified', 'alarm receiving centre notified'],

    createRequest: ['assistanceResponse.createRequest', 'Create assistance request'],

    assistanceRequest: ['assistanceRequest', 'Assistance request'], // reused
    message: ['message', 'Message'], // reused
    sentMeta: ['assistanceResponse.sentMeta', 'Sent {time} · {count} recipient(s)'],
    followUp: ['assistanceResponse.followUp', 'Follow-up'],
    followUpOf: ['assistanceResponse.followUpOf', 'Follow-up of "{name}"'],
    progressNeeded: ['assistanceResponse.progressNeeded', '{accepted} of {needed} needed'],
    progressAccepted: ['assistanceResponse.progressAccepted', '{accepted} of {total} accepted'],
    progressResponded: ['assistanceResponse.progressResponded', '{responded} of {total} responded'],
    followUpCounting: ['assistanceResponse.followUpCounting', 'Follow-up in {time} if fewer than {needed} accept'],
    followUpCountingNoTarget: ['assistanceResponse.followUpCountingNoTarget', 'Follow-up check in {time}'],
    followUpSent: ['assistanceResponse.followUpSent', 'Ended at {time}: too few accepted. Follow-up sent to {name}.'],
    followUpSentNoName: ['assistanceResponse.followUpSentNoName', 'Ended at {time}: too few accepted. Follow-up sent.'],
    followUpEnded: ['assistanceResponse.followUpEnded', 'Response window ended at {time}.'],
    targetReached: ['assistanceResponse.targetReached', 'Target reached: {accepted} of {needed} needed accepted.'],
    closedNote: ['assistanceResponse.closedNote', 'Closed at {time}. Recipients can no longer accept.'],
    closedWithAlarmNote: ['assistanceResponse.closedWithAlarmNote', 'Closed with the alarm at {time}. Recipients can no longer accept.'],
    expiredNote: ['assistanceResponse.expiredNote', 'Expired at {time}. Recipients can no longer accept.'],

    acceptedGroup: ['accepted', 'Accepted'], // reused
    rejectedGroup: ['rejected', 'Rejected'], // reused
    waitingGroup: ['assistanceResponse.waiting', 'No response yet'],
    didNotRespondGroup: ['assistanceResponse.didNotRespond', 'Did not respond'],
    failedGroup: ['assistanceResponse.notDelivered', 'Not delivered'],
    noResponse: ['assistanceResponse.noResponse', 'No response'],
    showAll: ['assistanceResponse.showAll', 'Show all {count}'],
    // Own keys: the generic `showLess` and `unknown` are not seeded on V4.
    showLess: ['assistanceResponse.showLess', 'Show less'],
    searchRecipients: ['assistanceResponse.searchRecipients', 'Search recipients'],
    noSearchResult: ['noSearchResult', 'No results'], // reused
    unknownRecipient: ['assistanceResponse.unknownRecipient', 'Unknown'],

    whatHappened: ['assistanceResponse.whatHappened', 'What happened'],
    alarmRaised: ['assistanceResponse.timeline.alarmRaised', '{type} raised by {name}'],
    alarmRaisedNoName: ['assistanceResponse.timeline.alarmRaisedNoName', '{type} raised'],
    locationFixed: ['assistanceResponse.timeline.locationFixed', 'Location fixed: {address}'],
    locationFixedNoAddress: ['assistanceResponse.timeline.locationFixedNoAddress', 'Location fixed'],
    arcNotifiedEvent: ['assistanceResponse.timeline.arcNotified', 'Alarm receiving centre notified'],
    arcFailedEvent: ['assistanceResponse.timeline.arcFailed', 'Forwarding to the alarm receiving centre failed'],
    requestSentEvent: ['assistanceResponse.timeline.requestSent', 'Assistance request sent to {name} ({count})'],
    requestSentEventNoName: ['assistanceResponse.timeline.requestSentNoName', 'Assistance request sent ({count})'],
    followUpSentEvent: ['assistanceResponse.timeline.followUpSent', 'Follow-up sent to {name} ({count})'],
    followUpSentEventNoName: ['assistanceResponse.timeline.followUpSentNoName', 'Follow-up sent ({count})'],
    acceptedEvent: ['assistanceResponse.timeline.accepted', '{name} accepted'],
    rejectedEvent: ['assistanceResponse.timeline.rejected', '{name} rejected'],
    targetReachedEvent: ['assistanceResponse.timeline.targetReached', 'Target reached for {name}'],
    targetReachedEventNoName: ['assistanceResponse.timeline.targetReachedNoName', 'Target reached'],
    requestClosedEvent: ['assistanceResponse.timeline.requestClosed', '{name} closed'],
    requestClosedEventNoName: ['assistanceResponse.timeline.requestClosedNoName', 'Assistance request closed'],
    requestExpiredEvent: ['assistanceResponse.timeline.requestExpired', '{name} expired'],
    requestExpiredEventNoName: ['assistanceResponse.timeline.requestExpiredNoName', 'Assistance request expired'],
    alarmClosedEvent: ['assistanceResponse.timeline.alarmClosed', 'Alarm closed'],
    alarmExpiredEvent: ['assistanceResponse.timeline.alarmExpired', 'Alarm expired'],

    closeAlsoClosesRequests: [
        'assistanceResponse.closeAlsoClosesRequests',
        'This also closes {count} open assistance request(s) ({names}). Recipients can no longer accept.',
    ],
    closeAlsoClosesRequestsNoNames: [
        'assistanceResponse.closeAlsoClosesRequestsNoNames',
        'This also closes {count} open assistance request(s). Recipients can no longer accept.',
    ],
    closeRequests: ['close.assistanceRequest', 'Close assistance request'], // reused
    closeRequestsExplanation: [
        'assistanceResponse.closeRequestsExplanation',
        'Closes {count} open assistance request(s) ({names}). Recipients can no longer accept.',
    ],
    closeRequestsExplanationNoNames: [
        'assistanceResponse.closeRequestsExplanationNoNames',
        'Closes {count} open assistance request(s). Recipients can no longer accept.',
    ],
    requestsClosed: ['assistanceResponse.requestsClosed', '{count} assistance request(s) closed'],
    requestsNotClosed: ['assistanceResponse.requestsNotClosed', '{count} assistance request(s) could not be closed'],

    enlargeMap: ['assistanceResponse.enlargeMap', 'Enlarge'],
    backToResponse: ['assistanceResponse.backToResponse', 'Back to response'],
};

export const situationText = (t, name, vars) => {
    const [key, defaultValue] = SITUATION_TEXT[name];
    return t(key, {defaultValue, ...vars});
};

const TONE = {
    [RESPONSE_STATUS.REACHED]: Colors.success,
    [RESPONSE_STATUS.PARTIAL]: Colors.orange,
    [RESPONSE_STATUS.ACCEPTED]: Colors.orange,
    [RESPONSE_STATUS.NONE_ACCEPTED]: Colors.danger,
    [RESPONSE_STATUS.NO_RECIPIENTS]: Colors.danger,
    [RESPONSE_STATUS.RESPONDED]: Colors.gray50,
};

export const responseStatusColor = (status) => TONE[status?.kind] || Colors.gray50;

// '' for a situation without requests: only what happened is shown, never an absence.
export function responseStatusText(t, status, {long = false} = {}) {
    if (!status) return '';
    const vars = {
        accepted: status.accepted,
        needed: status.needed,
        total: status.total,
        responded: status.responded,
    };
    switch (status.kind) {
        case RESPONSE_STATUS.REACHED:
            return situationText(t, long ? 'reachedCount' : 'reached', vars);
        case RESPONSE_STATUS.PARTIAL:
            return situationText(t, 'partial', vars);
        case RESPONSE_STATUS.NONE_ACCEPTED:
            return situationText(t, 'noneAccepted', vars);
        case RESPONSE_STATUS.ACCEPTED:
            return situationText(t, 'accepted', vars);
        case RESPONSE_STATUS.RESPONDED:
            return situationText(t, 'responded', vars);
        case RESPONSE_STATUS.NO_RECIPIENTS:
            return situationText(t, 'noRecipients', vars);
        default:
            return '';
    }
}

// '' when unnamed, so sentences take their no-name form; `t` is unused, kept to match requestLabel.
export function requestName(t, request, teamName) {
    if (request?.name) return request.name;
    const teamIds = new Set();
    (request?.receivers ? [
        ...request.receivers.accepted,
        ...request.receivers.rejected,
        ...request.receivers.waiting,
        ...request.receivers.failed,
    ] : []).forEach((r) => r.teamIds.forEach((id) => teamIds.add(String(id))));
    const teams = [...teamIds].map((id) => (teamName ? teamName(id) : '')).filter(Boolean);
    return teams.join(', ');
}

export function requestLabel(t, request, teamName) {
    return requestName(t, request, teamName) || situationText(t, 'assistanceRequest');
}

export function timelineText(t, entry, names) {
    const label = entry.request && names?.request ? names.request(entry.request) : '';
    const named = (name, vars) => situationText(t, label ? name : `${name}NoName`, {name: label, ...vars});
    switch (entry.kind) {
        case TIMELINE.ALARM_RAISED: {
            const type = t(entry.alarm.typeName);
            const name = names.origin(entry.alarm);
            return name
                ? situationText(t, 'alarmRaised', {type, name})
                : situationText(t, 'alarmRaisedNoName', {type});
        }
        case TIMELINE.LOCATION_FIXED:
            return entry.address
                ? situationText(t, 'locationFixed', {address: entry.address})
                : situationText(t, 'locationFixedNoAddress');
        case TIMELINE.ARC_NOTIFIED:
            return situationText(t, 'arcNotifiedEvent');
        case TIMELINE.ARC_FAILED:
            return situationText(t, 'arcFailedEvent');
        case TIMELINE.REQUEST_SENT:
            return named(entry.followUp ? 'followUpSentEvent' : 'requestSentEvent', {
                count: entry.request.receivers.total,
            });
        case TIMELINE.ACCEPTED:
            return situationText(t, 'acceptedEvent', {name: names.recipient(entry.deviceId)});
        case TIMELINE.REJECTED:
            return situationText(t, 'rejectedEvent', {name: names.recipient(entry.deviceId)});
        case TIMELINE.TARGET_REACHED:
            return named('targetReachedEvent');
        case TIMELINE.REQUEST_CLOSED:
            return named(entry.expired ? 'requestExpiredEvent' : 'requestClosedEvent');
        case TIMELINE.ALARM_CLOSED:
            return situationText(t, entry.expired ? 'alarmExpiredEvent' : 'alarmClosedEvent');
        default:
            return '';
    }
}

const TIMELINE_TONE = {
    [TIMELINE.ALARM_RAISED]: Colors.danger,
    [TIMELINE.ARC_FAILED]: Colors.danger,
    [TIMELINE.REJECTED]: Colors.danger,
    [TIMELINE.ACCEPTED]: Colors.success,
    [TIMELINE.TARGET_REACHED]: Colors.success,
    [TIMELINE.REQUEST_SENT]: Colors.blue,
};

export const timelineColor = (entry) => TIMELINE_TONE[entry?.kind] || Colors.gray40;
